// Resume-playback persistence.
// - Local (localStorage) cache always used so anon users + first paint work instantly.
// - Server (episode_resume_positions table) used when the user is signed in AND we
//   know the episode_id. Cross-device, survives browser changes.

import { supabase } from "@/integrations/supabase/client";

const KEY_PREFIX = "atp:resume:";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MIN_SAVE_SECONDS = 10;
const NEAR_END_SECONDS = 15;

type ResumeRecord = { t: number; d: number; ts: number };

const localKey = (src: string) => KEY_PREFIX + src;

export function getLocalResume(src?: string | null): number | null {
  if (!src || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(localKey(src));
    if (!raw) return null;
    const rec = JSON.parse(raw) as ResumeRecord;
    if (!rec || typeof rec.t !== "number") return null;
    if (Date.now() - rec.ts > TTL_MS) {
      window.localStorage.removeItem(localKey(src));
      return null;
    }
    if (rec.t < MIN_SAVE_SECONDS) return null;
    if (rec.d && rec.t > rec.d - NEAR_END_SECONDS) return null;
    return rec.t;
  } catch {
    return null;
  }
}

function writeLocal(src: string, t: number, d: number) {
  try {
    const rec: ResumeRecord = { t, d: d || 0, ts: Date.now() };
    window.localStorage.setItem(localKey(src), JSON.stringify(rec));
  } catch { /* ignore */ }
}

function removeLocal(src: string) {
  try { window.localStorage.removeItem(localKey(src)); } catch { /* ignore */ }
}

/** Fetch the server-side resume position for the signed-in user, if any. */
export async function fetchRemoteResume(episodeId?: string | null): Promise<number | null> {
  if (!episodeId) return null;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from("episode_resume_positions")
      .select("position_seconds, duration_seconds")
      .eq("user_id", user.id)
      .eq("episode_id", episodeId)
      .maybeSingle();
    if (error || !data) return null;
    const t = Number(data.position_seconds || 0);
    const d = Number(data.duration_seconds || 0);
    if (t < MIN_SAVE_SECONDS) return null;
    if (d && t > d - NEAR_END_SECONDS) return null;
    return t;
  } catch {
    return null;
  }
}

/** Throttled save: writes to localStorage immediately, and to the DB on demand. */
export function saveResume(
  src: string | null | undefined,
  episodeId: string | null | undefined,
  currentTime: number,
  duration: number,
) {
  if (!isFinite(currentTime)) return;
  if (currentTime < MIN_SAVE_SECONDS) return;
  if (duration && currentTime > duration - NEAR_END_SECONDS) {
    clearResume(src, episodeId);
    return;
  }
  if (src) writeLocal(src, currentTime, duration);
  void persistRemote(episodeId, currentTime, duration);
}

async function persistRemote(episodeId: string | null | undefined, t: number, d: number) {
  if (!episodeId) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      // Offline: queue, coalesced per-episode so we only sync the latest position.
      const { enqueue } = await import("./offlineQueue");
      await enqueue({
        kind: "resume_position",
        payload: { user_id: user.id, episode_id: episodeId, position_seconds: t, duration_seconds: d || null },
        dedupKey: `resume:${user.id}:${episodeId}`,
      });
      return;
    }
    const { error } = await supabase
      .from("episode_resume_positions")
      .upsert(
        {
          user_id: user.id,
          episode_id: episodeId,
          position_seconds: t,
          duration_seconds: d || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,episode_id" },
      );
    if (error) {
      const { enqueue, isNetworkError } = await import("./offlineQueue");
      if (isNetworkError(error)) {
        await enqueue({
          kind: "resume_position",
          payload: { user_id: user.id, episode_id: episodeId, position_seconds: t, duration_seconds: d || null },
          dedupKey: `resume:${user.id}:${episodeId}`,
        });
      }
    }
  } catch { /* ignore — local cache still works */ }
}

export function clearResume(src?: string | null, episodeId?: string | null) {
  if (src) removeLocal(src);
  if (!episodeId) return;
  (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase
        .from("episode_resume_positions")
        .delete()
        .eq("user_id", user.id)
        .eq("episode_id", episodeId);
    } catch { /* ignore */ }
  })();
}
